export interface Member {
  id: string;
  name: string;
  role: 'president' | 'member' | 'founder';
  joinedDate: string;
  booksRead: number;
  avatarInitials: string;
}

export interface BookCandidate {
  id: string;
  title: string;
  author: string;
  coverColor: string;
  nominatedBy: string;
  votes: number;
  synopsis: string;
  pageCount: number;
  genre: string;
}

export interface DiscussionThread {
  id: string;
  author: string;
  timestamp: string;
  title: string;
  body: string;
  replies: number;
  likes: number;
}

export interface ReadingHistoryEntry {
  id: string;
  title: string;
  author: string;
  coverColor: string;
  readDate: string;
  rating: number;
  pickedBy: string;
  cycle: number;
}

export interface ClubStats {
  name: string;
  motto: string;
  founded: string;
  totalBooksRead: number;
  averageRating: number;
  currentCycle: number;
  memberCount: number;
  nextMeetingDate: string;
}

export interface SampleData {
  club: ClubStats;
  members: Member[];
  currentBook: {
    title: string;
    author: string;
    coverColor: string;
    pageCount: number;
    genre: string;
    progress: number;
    dueDate: string;
    discussion: DiscussionThread[];
  };
  votingCandidates: BookCandidate[];
  readingHistory: ReadingHistoryEntry[];
}

export const SAMPLE_DATA: SampleData = {
  club: {
    name: 'The Pentarchy Book Club',
    motto: 'Five voices, one page at a time',
    founded: 'September 2024',
    totalBooksRead: 14,
    averageRating: 3.8,
    currentCycle: 15,
    memberCount: 5,
    nextMeetingDate: 'March 28, 2026',
  },

  members: [
    {
      id: 'm1',
      name: 'Elena Vargas',
      role: 'president',
      joinedDate: 'Sep 2024',
      booksRead: 14,
      avatarInitials: 'EV',
    },
    {
      id: 'm2',
      name: 'Marcus Okafor',
      role: 'founder',
      joinedDate: 'Sep 2024',
      booksRead: 13,
      avatarInitials: 'MO',
    },
    {
      id: 'm3',
      name: 'Sophie Chen',
      role: 'founder',
      joinedDate: 'Sep 2024',
      booksRead: 14,
      avatarInitials: 'SC',
    },
    {
      id: 'm4',
      name: 'David Holst',
      role: 'founder',
      joinedDate: 'Sep 2024',
      booksRead: 12,
      avatarInitials: 'DH',
    },
    {
      id: 'm5',
      name: 'Rina Takahashi',
      role: 'member',
      joinedDate: 'Jan 2025',
      booksRead: 10,
      avatarInitials: 'RT',
    },
  ],

  currentBook: {
    title: 'The Name of the Rose',
    author: 'Umberto Eco',
    coverColor: '#8B0000',
    pageCount: 536,
    genre: 'Historical Mystery',
    progress: 62,
    dueDate: 'March 28, 2026',
    discussion: [
      {
        id: 'd1',
        author: 'Sophie Chen',
        timestamp: '2 hours ago',
        title: 'The labyrinth as metaphor',
        body: "The library labyrinth is doing so much heavy lifting in this book. It's not just a physical maze \u2014 it's Eco telling us that knowledge itself is a maze, and the people who guard it are the real monsters.",
        replies: 4,
        likes: 3,
      },
      {
        id: 'd2',
        author: 'Marcus Okafor',
        timestamp: '5 hours ago',
        title: 'Adso as unreliable narrator?',
        body: "Anyone else notice how Adso keeps admitting he doesn't fully understand what William is doing? He's telling the story decades later and still confused. I think Eco is using that gap between experience and understanding on purpose.",
        replies: 6,
        likes: 5,
      },
      {
        id: 'd3',
        author: 'Elena Vargas',
        timestamp: '1 day ago',
        title: 'The debate scene in Chapter 3',
        body: "The theological debate between the Franciscans and the papal delegation is genuinely one of the most tense scenes I've read this year. Eco makes medieval politics feel like a thriller. The stakes are real even if the arguments are 700 years old.",
        replies: 3,
        likes: 4,
      },
    ],
  },

  votingCandidates: [
    {
      id: 'v1',
      title: 'Piranesi',
      author: 'Susanna Clarke',
      coverColor: '#4A90D9',
      nominatedBy: 'Rina Takahashi',
      votes: 3,
      synopsis:
        'A man lives in a vast, crumbling house filled with statues and tidal waters. He believes he is one of only two living inhabitants \u2014 but his journals suggest otherwise.',
      pageCount: 272,
      genre: 'Fantasy',
    },
    {
      id: 'v2',
      title: "If on a winter's night a traveler",
      author: 'Italo Calvino',
      coverColor: '#2E8B57',
      nominatedBy: 'Sophie Chen',
      votes: 2,
      synopsis:
        'You, the Reader, buy a novel \u2014 but the pages are misprinted. The quest to find the real book spirals into ten nested stories, each cut short at the moment of greatest tension.',
      pageCount: 260,
      genre: 'Postmodern Fiction',
    },
    {
      id: 'v3',
      title: 'The Master and Margarita',
      author: 'Mikhail Bulgakov',
      coverColor: '#8B4513',
      nominatedBy: 'David Holst',
      votes: 2,
      synopsis:
        'The Devil arrives in Soviet Moscow with a retinue of chaos agents \u2014 a giant cat, a fanged assassin, a naked witch. Meanwhile, Pontius Pilate wrestles with his conscience in ancient Jerusalem.',
      pageCount: 412,
      genre: 'Satire',
    },
    {
      id: 'v4',
      title: 'Beloved',
      author: 'Toni Morrison',
      coverColor: '#800020',
      nominatedBy: 'Marcus Okafor',
      votes: 1,
      synopsis:
        'A formerly enslaved woman is haunted by the ghost of the daughter she killed to save from slavery. The past is not past \u2014 it moves in, sits at the table, and demands to be fed.',
      pageCount: 324,
      genre: 'Literary Fiction',
    },
  ],

  readingHistory: [
    {
      id: 'h1',
      title: 'Dune',
      author: 'Frank Herbert',
      coverColor: '#C2A366',
      readDate: 'October 2024',
      rating: 4.2,
      pickedBy: 'Elena Vargas',
      cycle: 1,
    },
    {
      id: 'h2',
      title: 'Parable of the Sower',
      author: 'Octavia Butler',
      coverColor: '#556B2F',
      readDate: 'November 2024',
      rating: 4.6,
      pickedBy: 'Marcus Okafor',
      cycle: 2,
    },
    {
      id: 'h3',
      title: 'The Remains of the Day',
      author: 'Kazuo Ishiguro',
      coverColor: '#708090',
      readDate: 'December 2024',
      rating: 3.4,
      pickedBy: 'Sophie Chen',
      cycle: 3,
    },
    {
      id: 'h4',
      title: 'One Hundred Years of Solitude',
      author: 'Gabriel Garc\u00eda M\u00e1rquez',
      coverColor: '#DAA520',
      readDate: 'January 2025',
      rating: 4.8,
      pickedBy: 'David Holst',
      cycle: 4,
    },
    {
      id: 'h5',
      title: 'Station Eleven',
      author: 'Emily St. John Mandel',
      coverColor: '#4682B4',
      readDate: 'February 2025',
      rating: 3.6,
      pickedBy: 'Elena Vargas',
      cycle: 5,
    },
    {
      id: 'h6',
      title: "Giovanni's Room",
      author: 'James Baldwin',
      coverColor: '#B8860B',
      readDate: 'April 2025',
      rating: 4.4,
      pickedBy: 'Rina Takahashi',
      cycle: 6,
    },
    {
      id: 'h7',
      title: 'The Left Hand of Darkness',
      author: 'Ursula K. Le Guin',
      coverColor: '#2F4F4F',
      readDate: 'May 2025',
      rating: 3.8,
      pickedBy: 'Sophie Chen',
      cycle: 7,
    },
    {
      id: 'h8',
      title: 'Blindness',
      author: 'Jos\u00e9 Saramago',
      coverColor: '#F5F5DC',
      readDate: 'July 2025',
      rating: 4.0,
      pickedBy: 'Marcus Okafor',
      cycle: 8,
    },
    {
      id: 'h9',
      title: 'The Wind-Up Bird Chronicle',
      author: 'Haruki Murakami',
      coverColor: '#483D8B',
      readDate: 'September 2025',
      rating: 3.2,
      pickedBy: 'David Holst',
      cycle: 9,
    },
    {
      id: 'h10',
      title: 'Housekeeping',
      author: 'Marilynne Robinson',
      coverColor: '#5F9EA0',
      readDate: 'November 2025',
      rating: 4.0,
      pickedBy: 'Elena Vargas',
      cycle: 10,
    },
  ],
};
